// Tabla de SKUs de Inventario, pintada con el `DataTable` compartido, y sus controles de página.
import { useMemo } from 'react';
import type { Table } from '@tanstack/react-table';
import type { InventoryLensPeriod } from '@/api/services/retail-catalog/backoffice';
import { DataTablePagination } from '@/kit';
import { formatMxn, mxn } from '@/lib/dineroHelpers';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { columnsForPeriod, formatInt, NOT_APPLICABLE, skuCountLabel } from '../lib/inventoryFormat';
import {
    coverageDisplay,
    displayName,
    rowAlerts,
    tracksStock,
    type CoverageTone,
    type InventoryRow,
    type SortColumn,
    type SortState,
} from '../lib/inventoryList';
import { KindChip } from '@/shared/patterns/KindChip';
import { PromotionPill } from '@/shared/patterns/Promotions';
import { rowPromotionSummaries, rowUndetailedPromotion } from '../lib/inventoryPromotions';
import { StockAlertChip } from '@/shared/patterns/StockAlertChip';

const COVERAGE_CLASS: Record<CoverageTone, string> = {
    danger: 'font-bold text-danger',
    warning: 'font-bold text-warning',
    success: 'font-bold text-success',
    muted: 'font-semibold text-muted-foreground',
    none: 'italic text-muted-foreground',
};

const EMPTY = '—';
const CARD_NOT_APPLICABLE: ReadonlySet<SortColumn> = new Set(['sales', 'coverage', 'price']);

function isNotApplicable(column: SortColumn, row: InventoryRow): boolean {
    return row.kind === 'CARD' && CARD_NOT_APPLICABLE.has(column);
}

/** Todas las alertas de la fila, para el `title` de su celda. */
function alertsTitle(row: InventoryRow): string | undefined {
    const alerts = rowAlerts(row);
    return alerts.length === 0 ? undefined : alerts.map((a) => a.label).join(' · ');
}

/** La alerta más grave como chip de texto y, si hay más, «+N». */
function AlertsCell({ row }: { row: InventoryRow }) {
    const alerts = rowAlerts(row);
    if (alerts.length === 0 && row.kind === 'CARD')
        return <span className="text-muted-foreground">{NOT_APPLICABLE}</span>;
    if (alerts.length === 0) return <span className="text-muted-foreground/60">·</span>;
    const [first, ...rest] = alerts;
    return (
        <span className="inline-flex max-w-full items-center gap-1">
            <StockAlertChip alert={first.key} className="truncate">
                {first.label}
            </StockAlertChip>
            {rest.length > 0 ? (
                <span className="text-[10px] font-semibold text-muted-foreground">
                    <span aria-hidden>{`+${String(rest.length)}`}</span>
                    <span className="sr-only">
                        {`y ${String(rest.length)} alerta${rest.length === 1 ? '' : 's'} más`}
                    </span>
                </span>
            ) : null}
        </span>
    );
}

/** El nombre trunca primero; la píldora de promo vigente queda a su derecha. */
function NameCell({ row }: { row: InventoryRow }) {
    const promotions = rowPromotionSummaries(row);
    const undetailed = rowUndetailedPromotion(row);
    if (promotions.length === 0 && undetailed === undefined) {
        return <span className="font-semibold">{displayName(row)}</span>;
    }
    return (
        <span className="flex min-w-0 items-center gap-1">
            <span className="min-w-0 truncate font-semibold">{displayName(row)}</span>
            <PromotionPill
                promotions={promotions}
                undetailedReason={undetailed}
                compact
                className="flex-none"
            />
        </span>
    );
}

function cellOf(column: SortColumn, row: InventoryRow) {
    if (isNotApplicable(column, row)) return NOT_APPLICABLE;
    switch (column) {
        case 'name':
            return <NameCell row={row} />;
        case 'sku':
            return <span className="font-mono text-[10.5px]">{row.sku}</span>;
        case 'kind':
            return <KindChip kind={row.kind} />;
        case 'stock':
            return tracksStock(row) ? formatInt(row.stock) : EMPTY;
        case 'sales':
            return row.sales === null ? EMPTY : formatInt(row.sales);
        case 'coverage':
            return coverageDisplay(row.coverage, row.stock).text;
        case 'price':
            return row.priceCents === null ? EMPTY : formatMxn(mxn(row.priceCents));
        case 'alerts':
            return <AlertsCell row={row} />;
    }
}

function cellClassOf(column: SortColumn, row: InventoryRow): string | undefined {
    if (column === 'stock') return 'font-bold';
    // Solo el N/A va en gris; la alineación la da la columna, igual que en cualquier fila.
    if (isNotApplicable(column, row)) return 'text-muted-foreground';
    if (column === 'coverage') return COVERAGE_CLASS[coverageDisplay(row.coverage, row.stock).tone];
    return undefined;
}

/** Columnas del `DataTable`; el encabezado de ventas sigue el periodo. */
function tableColumns(period: InventoryLensPeriod): DataTableColumn<InventoryRow, SortColumn>[] {
    return columnsForPeriod(period).map((c) => ({
        key: c.column,
        // El encabezado parte en dos líneas antes que cortarse cuando la banda se angosta.
        label: <span className="whitespace-normal leading-tight">{c.label}</span>,
        width: c.width,
        numeric: c.numeric,
        sortKey: c.column,
        note: c.note,
        // Cifras y chip de clasificación nunca se cortan; nombre, SKU y alertas truncan con `title`.
        cellOverflow: c.numeric || c.column === 'kind' ? 'nowrap' : undefined,
        // Una cifra más ancha que su columna desborda hacia la izquierda: el borde derecho no se mueve.
        cell: (row) =>
            c.numeric ? (
                <span className="flex justify-end">{cellOf(c.column, row)}</span>
            ) : (
                cellOf(c.column, row)
            ),
        cellTitle:
            c.column === 'name'
                ? (row) => displayName(row)
                : c.column === 'sku'
                  ? (row) => row.sku
                  : c.column === 'alerts'
                    ? alertsTitle
                    : undefined,
        cellClassName: (row) => cellClassOf(c.column, row),
    }));
}

export function InventoryTable({
    table,
    pageRows,
    filteredCount,
    sort,
    onSortToggle,
    selectedSku,
    onSelect,
    isLoading,
    period,
}: {
    table: Table<InventoryRow>;
    /** Filas de la página visible, ya filtradas y ordenadas. */
    pageRows: InventoryRow[];
    filteredCount: number;
    sort: SortState;
    onSortToggle: (column: SortColumn) => void;
    selectedSku: string | null;
    onSelect: (sku: string) => void;
    isLoading: boolean;
    /** Periodo de la banda: las ventas de la tabla son de ese periodo. */
    period: InventoryLensPeriod;
}) {
    const columns = useMemo(() => tableColumns(period), [period]);
    return (
        <>
            <DataTable
                caption="SKUs"
                columns={columns}
                rows={pageRows}
                rowKey={(row) => row.sku}
                sort={{ key: sort.column, direction: sort.direction }}
                onSortToggle={onSortToggle}
                onRowClick={(row) => {
                    onSelect(row.sku);
                }}
                isRowSelected={(row) => row.sku === selectedSku}
                isLoading={isLoading}
                empty="Sin SKUs con los filtros seleccionados."
            />
            <div className="flex flex-none flex-wrap items-center justify-between gap-x-3 border-t border-border px-3">
                <span
                    aria-live="polite"
                    className="text-[10.5px] font-semibold text-muted-foreground"
                >
                    {isLoading ? '— SKUs' : skuCountLabel(filteredCount)}
                </span>
                <DataTablePagination table={table} />
            </div>
        </>
    );
}
